/**
 * Shared WebAuthn passkey registration helper used by both the Security
 * Settings page and the mandatory-MFA enrollment gate. Keeps the register
 * options→create→verify dance in one place.
 *
 * Returns { success: boolean, error?: string }.
 */
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';

export async function registerPasskey() {
  try {
    const { startRegistration } = await import('@simplewebauthn/browser');
    const optResp = await authFetch(`${API_URL}/api/auth/passkeys/register/options`, { method: 'POST' });
    if (!optResp.ok) {
      const e = await optResp.json().catch(() => ({}));
      throw new Error(e.detail || 'Could not start passkey setup');
    }
    const options = await optResp.json();
    const label = `${navigator.platform || 'Device'} · ${new Date().toLocaleDateString()}`;
    const cred = await startRegistration({ optionsJSON: options });
    const verifyResp = await authFetch(`${API_URL}/api/auth/passkeys/register/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...cred, state: options.state, label }),
    });
    if (!verifyResp.ok) {
      const e = await verifyResp.json().catch(() => ({}));
      throw new Error(e.detail || 'Passkey registration failed');
    }
    // Let any mounted MFA gate re-check enrollment immediately.
    try { window.dispatchEvent(new Event('corvana:mfa-refresh')); } catch (_) {}
    return { success: true };
  } catch (err) {
    const msg = err.name === 'NotAllowedError' ? 'Passkey setup cancelled' : (err.message || 'Passkey setup failed');
    return { success: false, error: msg };
  }
}
